import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { STATIC_EXCHANGES } from '../../config/constants/staticClassifications';
import { useBooleanMapToggle, useToggleSet } from '../useToggle';
import { useCountriesFromSecurities } from '../useCountriesFromSecurities';
import { useGICSExclusions } from './useGICSExclusions';
import { useClassificationData } from './useClassificationData';
import { useFilteredSecurities } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/useFilteredSecurities';
import { useSmartBetaStrategyStore } from '@/features/Strategies/stores/definitionFormStore';

// Stable empty-array reference for the useShallow selector fallback below.
// Returning a fresh `[]` from the selector makes every snapshot a new reference
// ("getSnapshot should be cached" → infinite render loop). Only bites when
// exclude_exchanges is absent — i.e. state persisted before the field existed.
const EMPTY_EXCHANGES: Array<string> = [];

// Country ↔ exchange maps for the geographic exclusion hierarchy (Region →
// Country → Exchange), modeled on the GICS sector → industry → sub-industry
// hierarchy: a parent exclusion implies its children, yet a child can be
// toggled back on its own — which demotes the parent to a "partial" state
// (the parent's own code drops, its remaining children stay excluded).
const EXCHANGES_BY_COUNTRY = new Map<string, Array<string>>();
const COUNTRY_BY_EXCHANGE = new Map<string, string>();
for (const e of STATIC_EXCHANGES) {
  const list = EXCHANGES_BY_COUNTRY.get(e.countryCode) ?? [];
  list.push(e.code);
  EXCHANGES_BY_COUNTRY.set(e.countryCode, list);
  COUNTRY_BY_EXCHANGE.set(e.code, e.countryCode);
}

// Stable empty set for the (now inert) GICS "disabled/greyed" prop — out-of-
// cyclicality sectors are HIDDEN via the visible* arrays, not greyed, so no GICS
// code is ever greyed. A module-level singleton keeps prop identity stable.
const EMPTY_DISABLED: Set<string> = new Set<string>();

// Which countries an index covers, from its STATIC coverage label (not the live
// snapshot): a single-country index resolves to that one country, the regional
// indices to their whole region, Global to everything. This is why US Target
// Market Exposure shows only the US even though its geographic scope is the
// whole of North America (which also contains Canada). An unknown label does
// not over-restrict.
function inIndexCoverage(
  c: { code: string; scope: string },
  coverageLabel?: string,
): boolean {
  switch (coverageLabel) {
    case 'United States':
      return c.code === 'US';
    case 'Canada':
      return c.code === 'CA';
    case 'Developed Markets (ex-NA)':
      return c.scope === 'dev_ex_na';
    case 'Emerging Markets':
      return c.scope === 'emerging';
    case 'Global Markets':
    default:
      return true;
  }
}

export function useSecurityTypeClassification(
  geographicScope: Array<string>,
  cyclicality: Array<string>,
  coverageLabel?: string,
) {
  const {
    sectors: allSectors,
    industries: allIndustries,
    subIndustries: allSubIndustries,
    securityTypes: allSecurityTypes,
  } = useClassificationData();
  const { countries: allCountries } = useCountriesFromSecurities();

  const {
    securities: filtered,
    totalCount,
    // Layer 3 (post-every-filter). Only `availableSectorCodes` is still read,
    // and only to be passed OUT for callers that legitimately want the
    // remaining population. Nothing in this hook may gate a control on it: a
    // control gated on Layer 3 is gated on the OTHER controls, which is how
    // excluding every country came to freeze the sector pickers.
    availableSectorCodes,
    // Layer 1 — what the selected universe contains, invariant under every
    // wizard filter. This is what may disable a control.
    universeSectorCodes,
    universeIndustryCodes,
    universeSubIndustryCodes,
    universeCountries,
    universeMics,
    securitiesUnavailable,
    filterExcludedCounts,
  } = useFilteredSecurities();

  // Scope classification data to only what exists in the selected universe
  // (Layer 1). When securities are unavailable (ClickHouse has no Morningstar
  // data) there is nothing to scope against, so we show the full static
  // catalog — these are static choices the user must be able to pick from to
  // build the config, independent of the live preview.
  // The catalogue is rendered WHOLE — like the GICS and geographic lists after
  // #1243, the full static security-type set is always shown and selectable, so
  // a point-in-time backtest can exclude a type the universe holds nothing for
  // today. What the universe actually holds no longer filters the list down.
  const securityTypesList = allSecurityTypes;
  // The catalogue is rendered WHOLE — the universe's own contents annotate it,
  // they no longer filter it.
  //
  // These three used to be intersected with what the selected universe holds in
  // the CURRENT snapshot, which silently made two legitimate things impossible.
  // A backtest is point-in-time: a run starting in 2005 covers data where
  // `10101001 Agricultural Inputs` existed, and the user could not exclude it
  // because it is absent from today's snapshot. And GECS 2026 is announced but
  // not yet in our feed, so Cannabis and Crypto Mining were unselectable — a
  // universe would silently GAIN them the day the revision lands, which is the
  // renumbering hazard in reverse.
  //
  // `universe*Codes` is still computed and still exported, as
  // `inUniverse*Codes`: what the universe holds today is guidance, not a veto.
  const sectors = allSectors;
  const industries = allIndustries;
  const subIndustries = allSubIndustries;

  const {
    excludeCountries,
    excludeTypes,
    excludeGicsSectorCodes,
    excludeGicsIndustryCodes,
    excludeGicsSubIndustryCodes,
    excludeExchanges,
    setExcludeCountries,
    setExcludeExchanges,
    setExcludeType,
    setExcludeGicsSector,
    setExcludeGicsIndustries,
    setExcludeGicsSubIndustries,
  } = useSmartBetaStrategyStore(
    useShallow((state) => ({
      excludeCountries: state.data_processor.exclude_countries,
      excludeTypes: state.data_processor.exclude_type,
      excludeGicsSectorCodes: state.data_processor.exclude_gics_sector,
      excludeGicsIndustryCodes: state.data_processor.exclude_gics_industries,
      excludeGicsSubIndustryCodes:
        state.data_processor.exclude_gics_sub_industries,
      excludeExchanges:
        state.data_processor.exclude_exchanges ?? EMPTY_EXCHANGES,
      setExcludeCountries: state.setExcludeCountries,
      setExcludeExchanges: state.setExcludeExchanges,
      setExcludeType: state.setExcludeType,
      setExcludeGicsSector: state.setExcludeGicsSector,
      setExcludeGicsIndustries: state.setExcludeGicsIndustries,
      setExcludeGicsSubIndustries: state.setExcludeGicsSubIndustries,
    })),
  );

  // Excluded count = scoped population (Layer 2) minus filtered (Layer 3)
  // Only reflects user-initiated exclusions, NOT scope/size differences
  const totalExcluded = totalCount - filtered.length;

  const securityTypes = useBooleanMapToggle({
    map: excludeTypes as unknown as Record<string, boolean>,
    onUpdate: setExcludeType as (
      patch: Partial<Record<string, boolean>>,
    ) => void,
  });

  // Country membership change → clear that country's individual exchange
  // exclusions. A newly-excluded country implies ALL its exchanges (kept
  // normalized — not stored), so any prior per-exchange partials are redundant;
  // a re-included country clears them too.
  const setCountriesWithCascade = useCallback(
    (nextCountries: Array<string>) => {
      const prevSet = new Set(excludeCountries);
      const nextSet = new Set(nextCountries);
      const changed = new Set<string>();
      for (const c of nextCountries) if (!prevSet.has(c)) changed.add(c);
      for (const c of excludeCountries) if (!nextSet.has(c)) changed.add(c);
      if (changed.size > 0) {
        const drop = new Set<string>();
        for (const c of changed)
          for (const ex of EXCHANGES_BY_COUNTRY.get(c) ?? []) drop.add(ex);
        const nextExchanges = excludeExchanges.filter((e) => !drop.has(e));
        if (nextExchanges.length !== excludeExchanges.length) {
          setExcludeExchanges(nextExchanges);
        }
      }
      setExcludeCountries(nextCountries);
    },
    [
      excludeCountries,
      excludeExchanges,
      setExcludeCountries,
      setExcludeExchanges,
    ],
  );

  // Toggle one exchange, hierarchy-aware (like a GICS sub-industry):
  //  • excluded via its country (implied) → INCLUDE it: drop the country and
  //    materialize its OTHER exchanges, so the country becomes "partial";
  //  • an explicit per-exchange exclusion → remove it;
  //  • otherwise → exclude just this exchange (country becomes "partial").
  const toggleExchange = useCallback(
    (code: string) => {
      const countryCode = COUNTRY_BY_EXCHANGE.get(code);
      const impliedByCountry =
        countryCode != null && excludeCountries.includes(countryCode);
      if (impliedByCountry) {
        const nextExchanges = new Set(excludeExchanges);
        for (const ex of EXCHANGES_BY_COUNTRY.get(countryCode) ?? []) {
          if (ex !== code) nextExchanges.add(ex);
        }
        setExcludeCountries(excludeCountries.filter((c) => c !== countryCode));
        setExcludeExchanges([...nextExchanges]);
        return;
      }
      const next = new Set(excludeExchanges);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      setExcludeExchanges([...next]);
    },
    [
      excludeCountries,
      excludeExchanges,
      setExcludeCountries,
      setExcludeExchanges,
    ],
  );

  // Geographic Reset clears countries AND exchanges (the whole filter).
  const clearGeo = useCallback(() => {
    setExcludeCountries([]);
    setExcludeExchanges([]);
  }, [setExcludeCountries, setExcludeExchanges]);

  const countries = {
    ...useToggleSet({
      initialValues: excludeCountries,
      onUpdate: setCountriesWithCascade,
    }),
    clearAll: clearGeo,
    // Securities dropped, not countries ticked — same correction as the
    // sector/industry badge above it.
    // Union of the country AND exchange sub-filters — the badge sits on the
    // header that owns both.
    excludedSecurityCount: filterExcludedCounts.geographicExcluded,
  };

  const exchanges = useMemo(
    () => ({ excluded: new Set(excludeExchanges), toggle: toggleExchange }),
    [excludeExchanges, toggleExchange],
  );

  const handleGICSUpdate = useCallback(
    (excluded: {
      sectors: Array<string>;
      industries: Array<string>;
      subIndustries: Array<string>;
    }) => {
      setExcludeGicsSector(excluded.sectors);
      setExcludeGicsIndustries(excluded.industries);
      setExcludeGicsSubIndustries(excluded.subIndustries);
    },
    [
      setExcludeGicsSector,
      setExcludeGicsIndustries,
      setExcludeGicsSubIndustries,
    ],
  );

  const gics = useGICSExclusions({
    sectors,
    industries,
    subIndustries,
    initialExcludedCodes: {
      sectors: excludeGicsSectorCodes,
      industries: excludeGicsIndustryCodes,
      subIndustries: excludeGicsSubIndustryCodes,
    },
    onUpdate: handleGICSUpdate,
  });

  // Countries within the selected Geographic Scope only — out-of-scope countries
  // are HIDDEN (not greyed), so they never clutter the picker or the exclusion
  // summary. Still scoped to the selected universe's countries when live data is
  // up. (Empty scope — shouldn't happen, the buttons enforce ≥1 — shows all.)
  const visibleCountries = useMemo(() => {
    const scopeSet = new Set(geographicScope);
    let list = [...allCountries].sort((a, b) => a.label.localeCompare(b.label));
    // Scope to the index's OWN coverage first (static coverageLabel, not the
    // live snapshot): a US index shows only the US, a Canada index only Canada,
    // the regional indices their whole region, Global everything. This is what
    // stops North-America-scoped US Target Market Exposure from listing Canada.
    list = list.filter((c) => inIndexCoverage(c, coverageLabel));
    // Geographic Scope is the USER's own choice on top of that — only meaningful
    // for a multi-region index. What the universe happens to hold today is not a
    // choice and does not narrow the list (see useCountriesFromSecurities: a
    // point-in-time backtest must still be able to exclude Russia).
    if (scopeSet.size > 0) list = list.filter((c) => scopeSet.has(c.scope));
    return list;
  }, [allCountries, geographicScope, coverageLabel]);

  // Exchanges: the static catalogue, scoped only to the countries the user's
  // Geographic Scope leaves visible.
  //
  // It used to be intersected with the venues the universe lists on TODAY,
  // which removed 33 of the 92 MICs — MISX and RTSX (Moscow, gone 2022-03-18),
  // XKAR (Karachi), the retired NYSE/AMEX codes, the OTC tiers. The reasoning
  // at the time was that offering a MIC holding no constituent is
  // indistinguishable from a broken filter. That is true for a LIVE screen and
  // false for a point-in-time backtest: a run over 2010–2022 covers Moscow
  // listings, and the user could not exclude them.
  //
  // So the venue list is complete, and `inUniverseMics` below marks what is
  // populated today instead of removing the rest.
  const visibleExchanges = useMemo(() => {
    const visibleCountryCodes = new Set(visibleCountries.map((c) => c.code));
    return STATIC_EXCHANGES.filter((e) =>
      visibleCountryCodes.has(e.countryCode),
    );
  }, [visibleCountries]);

  // Security types the UNIVERSE does not contain are disabled. Layer 1, not
  // Layer 3 — see `countriesNotInUniverse` below for why that distinction matters.
  // Nothing is disabled: the full static list is selectable (see
  // securityTypesList). A type the universe holds nothing for today is still a
  // valid point-in-time exclusion, so it is offered rather than greyed/hidden.
  const disabledSecurityTypes = EMPTY_DISABLED;

  // Disabled (greyed, still listed): countries the SELECTED UNIVERSE contains no
  // securities for. Out-of-scope countries are HIDDEN by visibleCountries above,
  // not greyed here.
  //
  // Layer 1 (`universeCountries`), NOT Layer 3 (`availableCountries`). Layer 3
  // is the population left after EVERY user filter, so it made the exclusion
  // groups poison each other: excluding every sector emptied Layer 3, which
  // greyed out every country, which left no way to undo — and symmetrically,
  // excluding every country locked the sector controls. A control must stay
  // usable no matter what the OTHER controls are set to; only the universe's
  // actual contents may disable it, and those don't move.
  // Countries the universe holds nothing for TODAY. This MARKS them; it no
  // longer disables them. A point-in-time backtest may legitimately exclude a
  // country the index has since dropped, and greying the row made that
  // impossible without saying so.
  const countriesNotInUniverse = useMemo(() => {
    const marked = new Set<string>();
    if (securitiesUnavailable) return marked;
    for (const c of allCountries) {
      if (!universeCountries.has(c.code)) marked.add(c.code);
    }
    return marked;
  }, [allCountries, universeCountries, securitiesUnavailable]);

  // Only user-initiated GICS exclusions — no auto-exclusion.
  // Items with 0 securities are hidden via disabledCodes in the UI.
  const effectiveGicsExcluded = gics.excluded;

  // The catalogue is complete, so EVERY code in it is toggleable. No gate.
  //
  // This used to filter candidates against Layer 1 (`universe*Codes`), which
  // was right while the catalogue WAS the universe. Since the lists became the
  // full point-in-time union, that gate silently swallowed the click on every
  // row the universe holds nothing for today — retired industries, the GECS
  // 2026 codes, ~170 of the 316. Rendered, hoverable, and inert: the worst
  // possible failure, because nothing tells the user their click did anything.
  //
  // It is also self-defeating. Those rows exist precisely so a point-in-time
  // backtest can exclude something the index has since dropped; gating them on
  // "does the universe hold it today" removes the only reason they are there.
  //
  // What the universe holds is conveyed by the live dot, which informs without
  // deciding.
  const effectiveGicsToggle = gics.toggle;

  // Cyclicality is an independent universe selector — it does not cascade into
  // (auto-exclude) the sector exclusions. Sectors whose super-sector is outside
  // the selected cyclicality — and their industries + sub-industries — are HIDDEN
  // (removed from the visible* arrays below), not greyed, so they never appear in
  // the picker or the exclusion summary. Empty / all-three selected = no
  // constraint (nothing hidden).
  const hiddenGicsCodes = useMemo(() => {
    const hidden = new Set<string>();
    const cycSet = new Set(cyclicality);
    if (cycSet.size === 0 || cycSet.size >= 3) return hidden;
    const hiddenSectorCodes = new Set<string>();
    for (const s of sectors) {
      if (!cycSet.has(s.superSector)) {
        hidden.add(s.code);
        hiddenSectorCodes.add(s.code);
      }
    }
    for (const i of industries) {
      if (hiddenSectorCodes.has(i.sectorCode)) hidden.add(i.code);
    }
    for (const si of subIndustries) {
      if (hiddenSectorCodes.has(si.sectorCode)) hidden.add(si.code);
    }
    return hidden;
  }, [cyclicality, sectors, industries, subIndustries]);

  // The GICS lists the UI renders — out-of-cyclicality entries removed. The
  // exclusion summary derives from these too, so hidden codes never show there.
  const visibleGicsSectors = useMemo(
    () => sectors.filter((s) => !hiddenGicsCodes.has(s.code)),
    [sectors, hiddenGicsCodes],
  );
  const visibleGicsIndustries = useMemo(
    () => industries.filter((i) => !hiddenGicsCodes.has(i.code)),
    [industries, hiddenGicsCodes],
  );
  const visibleGicsSubIndustries = useMemo(
    () => subIndustries.filter((si) => !hiddenGicsCodes.has(si.code)),
    [subIndustries, hiddenGicsCodes],
  );

  return {
    // What the selected universe holds in the CURRENT snapshot, at every level.
    // The catalogues are rendered WHOLE; these MARK what is populated today
    // rather than hiding everything else, so a point-in-time backtest can still
    // exclude a retired industry, a GECS 2026 one, Russia or a Moscow listing.
    inUniverseSectorCodes: universeSectorCodes,
    inUniverseIndustryCodes: universeIndustryCodes,
    inUniverseSubIndustryCodes: universeSubIndustryCodes,
    inUniverseCountries: universeCountries,
    inUniverseMics: universeMics,
    // Union of the stocks this card's own sub-filters remove (type ∪ GICS ∪
    // country), counted once each.
    classificationExcludedCount:
      filterExcludedCounts.classificationGroupExcluded,
    primaryShareClassExcludedCount:
      filterExcludedCounts.primaryShareClassExcluded,
    securityTypes: {
      ...securityTypes,
      // Securities the type exclusions drop — the header badge, same one the
      // GICS and geographic headers carry.
      excludedSecurityCount: filterExcludedCounts.typeExcluded,
    },
    gics: {
      ...gics,
      excluded: effectiveGicsExcluded,
      toggle: effectiveGicsToggle,
      // Securities dropped, not boxes ticked. The header badge used to render
      // `excluded.size` — with one sector, five industry groups and ten
      // industries selected it read "16 excluded", which is a count of the
      // user's own clicks and tells them nothing about the universe.
      excludedSecurityCount: filterExcludedCounts.gicsExcluded,
    },
    countries,
    visibleCountries,
    countriesNotInUniverse,
    exchanges,
    visibleExchanges,
    totalExcluded,
    securityTypeItems: securityTypesList,
    disabledSecurityTypes,
    gicsSectors: visibleGicsSectors,
    gicsIndustries: visibleGicsIndustries,
    gicsSubIndustries: visibleGicsSubIndustries,
    // Out-of-cyclicality codes are hidden via the arrays above, so nothing is
    // greyed; the prop stays for the (currently unused) live-data 0-securities
    // case, mirroring countriesNotInUniverse.
    disabledGicsCodes: EMPTY_DISABLED,
    availableSectorCodes,
  };
}
